import React, { ChangeEventHandler, FC, useCallback, useEffect, useRef, useState } from 'react';
import { CountdownCircleTimer, Props, TimeProps } from 'react-countdown-circle-timer';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { settingsActions } from '../../../../store/slices/settings.slice';
import { teamActions } from '../../../../store/slices/team.slice';

import {
  ClockContainer,
  ClockTime,
  IClockContainer,
  LittleClockContainer,
  StyledInput,
  TimeContainer,
} from './styles';

export type ITimerClock = Omit<
  IClockContainer & Props & { title: string },
  'colors' | 'size' | 'rotation'
>;

const TimerClock: FC<ITimerClock> = ({
  $isDark,
  $isActive,
  $isBlinking,
  $isMainClock,
  title,
  ...props
}) => {
  const [timeStr, setTimeStr] = useState('');
  const [isActiveBlinking, setIsActiveBlinking] = useState(false);
  const [editTimeStr, setEditTimeStr] = useState('');
  const [focus, setFocus] = useState(false);
  const dispatch = useAppDispatch();
  const player = title === 'Player 1' || title === 'Team 1' ? 'player1' : 'player2';
  const [min, setMin] = useState<number | null>(null);
  const [sec, setSec] = useState<number | null>(null);

  const onBlurInput = () => {
    setFocus(false);
    const oldTimeParts = timeStr.split(':');
    dispatch(teamActions.startPause({ player }));
    dispatch(teamActions.completeGame());
    if (player === 'player1') {
      dispatch(teamActions.updateTimerPlayer1());
    } else {
      dispatch(teamActions.updateTimerPlayer2());
    }
    dispatch(
      settingsActions.setPartialDuel({
        player: player,
        settings: {
          min: min ?? +oldTimeParts[0],
          sec: sec ?? +oldTimeParts[1],
        },
      }),
    );
  };

  const onChangeTime: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    const value = e.target.value;
    const time = value.substring(0, 2) + ':' + value.substring(3, 5);

    const timeParts = time.split(':');

    setEditTimeStr(e.target.value);
    setMin(+timeParts[0]);
    setSec(+timeParts[1]);
  }, []);

  const onClickTime: React.MouseEventHandler<HTMLElement> = useCallback((event) => {
    setFocus(true);
    setEditTimeStr(event.currentTarget.textContent ?? '');
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const keyEvent = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        onBlurInput();
      }
    };

    document.addEventListener('keydown', keyEvent);

    return () => {
      document.removeEventListener('keydown', keyEvent);
    };
  }, [onBlurInput]);

  const renderTime = useCallback(
    ({ remainingTime }: TimeProps) => {
      if (remainingTime <= 10) {
        setIsActiveBlinking(true);
      }

      const minutes = Math.floor(remainingTime / 60);
      const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;

      const seconds = Math.floor(remainingTime - minutes * 60);
      const displaySeconds = seconds < 10 ? `0${seconds}` : seconds;
      setTimeStr(`${displayMinutes}:${displaySeconds}`);
      return (
        <ClockTime
          $isDark={$isDark}
          $isMainClock={$isMainClock}
          onClick={onClickTime}
        >
          {displayMinutes}:{displaySeconds}
        </ClockTime>
      );
    },
    [$isDark],
  );

  const validKeyForPayment = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    'Backspace',
    ':',
    'ArrowLeft',
    'ArrowRight',
  ];

  return (
    <ClockContainer
      $isDark={$isDark}
      $isBlinking={isActiveBlinking && $isBlinking}
    >
      {!focus ? (
        <>
          <TimeContainer $isActive={$isActive}>
            <CountdownCircleTimer
              {...props}
              colors={'#ffffff' as any}
              size={$isMainClock ? 0 : 260}
              isGrowing={$isMainClock ? false : true}
              rotation="counterclockwise"
            >
              {renderTime}
            </CountdownCircleTimer>
          </TimeContainer>
          <LittleClockContainer
            $isActive={$isActive}
            onClick={onClickTime}
          >
            {editTimeStr ? editTimeStr : timeStr}
          </LittleClockContainer>
        </>
      ) : (
        <StyledInput
          type="text"
          maxLength={5}
          value={editTimeStr}
          onChange={onChangeTime}
          onBlur={onBlurInput}
          onDoubleClick={onBlurInput}
          autoFocus
          onKeyDown={(e: any) => {
            if (!validKeyForPayment.includes(e.key)) {
              e.preventDefault();
            }
          }}
        />
      )}
    </ClockContainer>
  );
};

export default TimerClock;
